import { css } from '@emotion/react';
import { faFileExport } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useMemo,
} from 'react';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { RoundButtonWithTextComponent } from '~/_shared/baseComponents/buttons/roundButton/roundButtonWithText.component';
import { ExportImageLegendMode } from '~/_shared/types/exportImage/exportImage';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { useTranslation } from '~/_shared/utils/hooks';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import {
  useBoundaryGroupsIsLoadingSelector,
  useBoundaryGroupsSelector,
  useCustomBoundaryGroupsSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryDetailsIsLoadingSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import {
  useBoundaryTerritoryGroupsIsLoadingSelector,
  useBoundaryTerritoryGroupsSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { useBoundaryConvertInProgressSelector } from '~/store/frontendState/mapTools/boundary/convertBoundaries/convertBoundaries.selectors';
import { useMapSettingsExportImageSettingsSelector } from '~/store/mapSettings/toolsState/exportImageSettings/exportImageSettings.selectors';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import { useOptimizedWMSTerritoriesEnabledSelector } from '~/store/userData/userData.selectors';
import { useIsNumericColumnCheck } from '../../../../heatMap/panel/form/useIsNumericColumnCheck.hook';
import { useToolCollapseState } from '../useToolCollapseState';
import { BoundaryToolComponent } from './boundaryTool.component';
import { type BoundaryAction } from './boundaryTools.types';
import { BoundaryActionType } from './loadBoundaryPane/loadBoundaryPane.component';

const exportBtnStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  height: '100%',
});

export const BoundaryToolContainer: FC = () => {
  const isBoundaryConvertInProgress = useBoundaryConvertInProgressSelector();
  const isBoundaryExportLocationsAllowed = useIsDataExportAllowed(MapTool.Boundary);
  const isBoundaryGroupsLoading = useBoundaryGroupsIsLoadingSelector();
  const isBoundaryTerritoryDetailsLoading = useBoundaryTerritoryDetailsIsLoadingSelector();
  const isBoundaryTerritoryGroupsLoading = useBoundaryTerritoryGroupsIsLoadingSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const { openModal: openBoundaryGroupsListingModal } = useModal(ModalType.BoundaryGroupsListing);
  const { openModal: openBoundaryTerritoryGroupExportModal } = useModal(ModalType.ExportBoundaryTerritoryGroupData);
  const { openModal: openUpgradeWMSSubscriptionModal } = useModal(ModalType.WMSUpgradeSubscription);
  const { checkIsNumeric, isLoading: isCheckNumericLoading } = useIsNumericColumnCheck();
  const customBoundaryGroups = useCustomBoundaryGroupsSelector();
  const [t] = useTranslation();
  const optimizedTerritoriesEnabled = useOptimizedWMSTerritoriesEnabledSelector();
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const legendMode = useMapSettingsExportImageSettingsSelector().legendMode === ExportImageLegendMode.Boundary;
  const { isToolCollapsed } = useToolCollapseState();

  const boundaryActions: BoundaryAction[] = useMemo(() => {
    let actions = [{
      name: t('Load Geographic Boundary'),
      value: BoundaryActionType.LoadGeographicBoundary,
    }];

    if (!isEditModeDisabled) {
      actions = actions.concat([{
        name: t('Load Saved Territory Group'),
        value: BoundaryActionType.LoadSavedTerritoryGroup,
      }, {
        name: t('Create Territories'),
        value: BoundaryActionType.CreateTerritories,
      }]);
    }

    return actions;
  }, [isEditModeDisabled, t]);

  const handleManageBoundaryGroupsClick = !isEditModeDisabled ? () => {
    openBoundaryGroupsListingModal();
  } : undefined;

  const mapHasOnlyAIGeneratedBTG = boundaryTerritoryGroups.length > 0 && boundaryTerritoryGroups.every((btg) => {
    const matchingBoundaryGroup = boundaryGroups.find(boundaryGroup => boundaryGroup.id === btg.boundaryGroupId);
    return !!matchingBoundaryGroup?.wms.territoriesCount;
  });

  const handleExportFile = useCallback(() => {
    if (!optimizedTerritoriesEnabled && mapHasOnlyAIGeneratedBTG) {
      openUpgradeWMSSubscriptionModal();
    }
    else {
      openBoundaryTerritoryGroupExportModal({
        selectedBoundaryTerritoryGroupIds: [],
        showSelectBoundaryTerritoryGroupsSection: true,
      });
    }
  }, [openBoundaryTerritoryGroupExportModal, optimizedTerritoriesEnabled, mapHasOnlyAIGeneratedBTG, openUpgradeWMSSubscriptionModal]);

  const isLoading = isBoundaryGroupsLoading
  || isBoundaryTerritoryGroupsLoading
  || isBoundaryTerritoryDetailsLoading
  || isCheckNumericLoading
  || isBoundaryConvertInProgress;

  const exportBtn = isBoundaryExportLocationsAllowed ? (
    <div css={exportBtnStyle}>
      <RoundButtonWithTextComponent
        buttonStyle={!optimizedTerritoriesEnabled && mapHasOnlyAIGeneratedBTG ? RoundButtonStyle.RestrictedInverted : RoundButtonStyle.Quaternary}
        icon={faFileExport}
        onClick={handleExportFile}
        text={t('Export File')}
      />
    </div>
  ) : undefined;

  const showAdminControls = !isEditModeDisabled && !isToolCollapsed;

  return (
    <BoundaryToolComponent
      legendMode={legendMode}
      actions={boundaryActions}
      checkIsNumeric={checkIsNumeric}
      isLoading={isLoading}
      exportBtn={exportBtn}
      onManageBoundaryGroupsClick={showAdminControls && customBoundaryGroups.length > 0 ? handleManageBoundaryGroupsClick : undefined}
      showAdminControls={showAdminControls}
      showBoundaryLoadOptions={!isEditModeDisabled && !isToolCollapsed}
    />
  );
};
